import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SERVICE_STEPS = [
  'atualizacao_cadastral',
  'atualizacao_endereco',
  'atualizacao_dados_pessoais',
  'vida_financeira',
  'verificar_email',
  'consulta_seguro',
  'antes_rating_inicial',
  'cadastro_estruturacao',
  'depois_rating_final'
];

const STEP_LABELS = {
  atualizacao_cadastral: 'Atualização Cadastral',
  atualizacao_endereco: 'Atualização de Endereço',
  atualizacao_dados_pessoais: 'Atualização dos Dados Pessoais',
  vida_financeira: 'Vida Financeira',
  verificar_email: 'Verificar E-mail',
  consulta_seguro: 'Consulta de Seguro',
  antes_rating_inicial: 'ANTES (Rating Inicial)',
  cadastro_estruturacao: 'CADASTRO (Estruturação)',
  depois_rating_final: 'DEPOIS (Rating Final)'
};

// Componente Modal do Checklist
function ProductionLineModal({ lead, onClose, onUpdate }) {
  const [steps, setSteps] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (lead && lead.service_steps) {
      setSteps(lead.service_steps);
    }
  }, [lead]);

  // Validação de segurança para lead undefined
  if (!lead) {
    return (
      <div className="production-line-container">
        <div className="production-line-header">
          <h2>Esteira de Produção</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="lead-info">
          <p className="error-message">Nenhum cliente selecionado</p>
        </div>
        <style jsx>{`
          .production-line-container {
            background: white;
            border-radius: 8px;
            padding: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            max-width: 600px;
            margin: 0 auto;
          }
          .production-line-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 1px solid #e0e0e0;
          }
          .production-line-header h2 {
            margin: 0;
            color: #333;
            font-size: 20px;
          }
          .close-btn {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .close-btn:hover {
            color: #333;
          }
          .lead-info {
            background: #f5f5f5;
            padding: 15px;
            border-radius: 4px;
            margin-bottom: 20px;
          }
          .error-message {
            background: #fee;
            color: #c33;
            padding: 10px;
            border-radius: 4px;
            margin: 0;
            font-size: 14px;
          }
        `}</style>
      </div>
    );
  }

  const handleStepToggle = async (stepKey) => {
    const newSteps = {
      ...steps,
      [stepKey]: !steps[stepKey]
    };
    
    setSteps(newSteps);
    setLoading(true);
    setError(null);

    try {
      const response = await axios.put(`/api/service-steps/lead/${lead.id}`, {
        steps: newSteps
      });

      if (response.data && response.data.lead && onUpdate) {
        onUpdate(response.data.lead);
      }

      // Verificar se todos os passos foram concluídos
      const allCompleted = SERVICE_STEPS.every(step => newSteps[step] === true);
      if (allCompleted) {
        console.log('Todos os passos concluídos - Lead movido para Serviço Completo');
      }
    } catch (err) {
      setError('Erro ao atualizar passo do serviço');
      console.error('Erro ao atualizar passo:', err);
      // Reverter mudança em caso de erro
      setSteps(steps);
    } finally {
      setLoading(false);
    }
  };

  const completedCount = SERVICE_STEPS.filter(step => steps[step] === true).length;
  const progress = (completedCount / SERVICE_STEPS.length) * 100;

  return (
    <div className="production-line-container">
      <div className="production-line-header">
        <h2>Esteira de Produção</h2>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="lead-info">
        <p><strong>Cliente:</strong> {lead?.nome || 'Cliente Sem Nome'}</p>
        <p><strong>CPF/CNPJ:</strong> {lead?.cpf_cnpj || 'Não informado'}</p>
        <p><strong>Status:</strong> {lead?.status || 'Desconhecido'}</p>
      </div>

      <div className="progress-bar-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="progress-text">{completedCount}/{SERVICE_STEPS.length} passos concluídos ({progress.toFixed(0)}%)</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="checklist-container">
        {SERVICE_STEPS.map(stepKey => (
          <div key={stepKey} className="checklist-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={steps[stepKey] === true}
                onChange={() => handleStepToggle(stepKey)}
                disabled={loading}
              />
              <span className={steps[stepKey] ? 'completed' : ''}>
                {STEP_LABELS[stepKey]}
              </span>
            </label>
          </div>
        ))}
      </div>

      <div className="production-line-footer">
        <p className="info-text">
          Ao concluir todos os itens, o status do cliente mudará automaticamente para "Serviço Completo".
        </p>
      </div>

      <style jsx>{`
        .production-line-container {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          max-width: 600px;
          margin: 0 auto;
        }

        .production-line-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e0e0e0;
        }

        .production-line-header h2 {
          margin: 0;
          color: #333;
          font-size: 20px;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover {
          color: #333;
        }

        .lead-info {
          background: #f5f5f5;
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .lead-info p {
          margin: 5px 0;
          color: #555;
        }

        .progress-bar-container {
          margin-bottom: 20px;
        }

        .progress-bar {
          height: 20px;
          background: #e0e0e0;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 5px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #4CAF50, #8BC34A);
          transition: width 0.3s ease;
        }

        .progress-text {
          margin: 0;
          font-size: 14px;
          color: #666;
          text-align: center;
        }

        .error-message {
          background: #fee;
          color: #c33;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
          font-size: 14px;
        }

        .checklist-container {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .checklist-item {
          display: flex;
          align-items: center;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          user-select: none;
        }

        .checkbox-label input[type="checkbox"] {
          width: 20px;
          height: 20px;
          margin-right: 10px;
          cursor: pointer;
        }

        .checkbox-label span {
          font-size: 15px;
          color: #333;
          transition: color 0.2s;
        }

        .checkbox-label span.completed {
          color: #4CAF50;
          text-decoration: line-through;
        }

        .checkbox-label input[type="checkbox"]:disabled {
          cursor: not-allowed;
        }

        .production-line-footer {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #e0e0e0;
        }

        .info-text {
          margin: 0;
          font-size: 13px;
          color: #666;
          text-align: center;
          font-style: italic;
        }
      `}</style>
    </div>
  );
}

// Página Principal da Esteira de Produção
function ProductionLine() {
  const { darkMode } = useSettings();
  const [clients, setClients] = useState([]);
  const [checklists, setChecklists] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchChecklists();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads?status=concluido`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklists = async () => {
    try {
      const response = await axios.get(`${API_URL}/rating-checklist`);
      const checklistMap = {};
      if (response.data && response.data.checklists) {
        response.data.checklists.forEach(checklist => {
          checklistMap[checklist.lead_id] = checklist;
        });
      }
      setChecklists(checklistMap);
    } catch (error) {
      console.error('Erro ao buscar checklists:', error);
    }
  };

  const calculateProgress = (checklist) => {
    if (!checklist) return 0;
    const items = [
      'atualizacao_cadastral', 'atualizacao_endereco', 'atualizacao_dados_pessoais',
      'vida_financeira', 'verificar_email', 'consulta_seguro', 'antes', 'cadastro', 'depois'
    ];
    const completed = items.filter(item => checklist[item] === 1).length;
    return Math.round((completed / items.length) * 100);
  };

  const handleOpenModal = (client) => {
    setSelectedLead(client);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedLead(null);
    setShowModal(false);
  };

  const handleUpdate = (updatedLead) => {
    setClients(clients.map(c => c.id === updatedLead.id ? updatedLead : c));
    fetchChecklists();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={darkMode ? 'text-white' : 'text-gray-500'}>Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Esteira de Produção</h1>
        <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Gerencie o checklist de serviços dos clientes</p>
      </div>

      {clients.length === 0 ? (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-12 text-center`}>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Nenhum cliente em produção</p>
        </div>
      ) : (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
          <table className="w-full">
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr className={`text-left text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <th className="px-6 py-3">Nome</th>
                <th className="px-6 py-3">CPF/CNPJ</th>
                <th className="px-6 py-3">Tipo</th>
                <th className="px-6 py-3">Progresso</th>
                <th className="px-6 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => {
                const checklist = checklists[client.id];
                const progress = calculateProgress(checklist);
                return (
                  <tr key={client.id} className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                    <td className={`px-6 py-4 font-medium ${darkMode ? 'text-white' : ''}`}>
                      {client?.nome || 'Cliente Sem Nome'}
                    </td>
                    <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {client?.cpf_cnpj || 'Não informado'}
                    </td>
                    <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        client.tipo_pessoa === 'PF' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {client.tipo_pessoa === 'PF' ? 'PF' : 'PJ'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all bg-blue-600"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {progress}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleOpenModal(client)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Abrir Checklist
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ProductionLineModal
            lead={selectedLead}
            onClose={handleCloseModal}
            onUpdate={handleUpdate}
          />
        </div>
      )}
    </div>
  );
}

export default ProductionLine;
